<script setup lang="ts">
import type { AddressForm, AddressSearchResult, CustomerDto, CustomerResponse } from '@/types';
import { computed, ref, watch } from 'vue';
import InputCardComponent from '@/components/InputCardComponent.vue';
import HelpTooltipComponent from '@/components/HelpTooltipComponent.vue';
import { useCustomerStore } from '@/stores/customer';
import { useMessageStore } from '@/stores/messageStore';

// address1, zipCode and city are set here, the rest of the customer is set by ContactInfoComponent
const customerForm = defineModel<Partial<CustomerDto>>('customerForm', { required: true })
const addressForm = defineModel<AddressForm>('addressForm', { required: true })

const customerStore = useCustomerStore()
const message = useMessageStore()

// The API allows 73 characters of tekst, and ", 0000" (the postnummer) is added to what is typed
const tekstMaxLength = 73 - ', 0000'.length

// Shown when hovering the "i" next to each field
const helpTexts = {
  postnummer: 'Dette felt er påkrævet. Skriv dit postnummer (4 cifre), hvilket er med til at filtrere valget af adresser.',
  tekst: 'Dette felt er påkrævet. Skriv adressen uden postnummer, f.eks. "Adresse vej 42B, 9, th". Jo mere af adressen du skriver, jo færre adresser vises.',
  adresse: 'Dette felt skal vælges, før man er i stand til at gå videre. Der er flere muligheder, der kan vises i dette felt. Hvis man ser en pil til højre, er det muligt at gå ind på adressen og se flere bolig-/lejlighedsmuligheder. Når man endelig har valgt en adresse, bliver den skrevet i felterne ovenfor, og listen forsvinder. Ændrer man postnummer eller adresse, kommer listen igen.'
}

const addressOptions = ref<AddressSearchResult[]>([])
const searching = ref(false)
const searched = ref(false)
const rules = ref({
  required: (value: unknown) => !!value || "Påkrævet",
  postnummer: (value?: string | null) =>
    /^\d{4}$/.test(value ?? '') ||
    "Skal være 4 cifre.",
  // A picked or found address puts its adresselinje in the field, which can be longer than what can be typed
  tekst: (value: string) =>
    (!!value && (value.length <= tekstMaxLength || hasAddress.value)) ||
    `Må maksimalt være ${tekstMaxLength} tegn.`,
})

// Picked from the list, or the address of the customer found by email. Either way there is nothing to look up
const hasAddress = computed(() => !!addressForm.value.selected || !!addressForm.value.fromCustomer)

// Postnummer first, only then can the address be typed and the API be called
const postnummerValid = computed(() => rules.value.postnummer(addressForm.value.postnummer) === true)
const canSearch = computed(() => postnummerValid.value && rules.value.tekst(addressForm.value.tekst ?? '') === true)

// Stops letters from being typed or pasted into the postnummer. Changing the value afterwards wouldn't update what the field shows
function onlyDigits(event: InputEvent) {
  const text = event.data ?? event.dataTransfer?.getData('text')

  if (text && /\D/.test(text)) {
    event.preventDefault()
  }
}

let searchTimeout: ReturnType<typeof setTimeout> | undefined
let lastSearch = 0

async function searchAddresses() {
  // The picked or found address is in the fields, so the list is hidden. Changing a field clears a picked one and searches again
  if (hasAddress.value) {
    lastSearch++ // Drop the answer to a search still on its way, so it can't bring the list back
    addressOptions.value = []
    searched.value = false
    searching.value = false
    return
  }

  if (!canSearch.value) {
    addressOptions.value = []
    searched.value = false
    return
  }

  const { tekst, postnummer } = addressForm.value
  const search = ++lastSearch

  searching.value = true

  try {
    // The postnummer is added last to what was typed, e.g. "Adresse vej 42B, 9, th, 0001"
    const result = await customerStore.searchAddresses({ tekst: `${tekst}, ${postnummer}` })

    // Ignore answers to older searches that arrive after a newer one
    if (search === lastSearch) {
      addressOptions.value = result
      searched.value = true
    }
  } catch (error) {
    console.error(error)

    if (search === lastSearch) {
      message.addError('Der skete en fejl ved at hente adresser')
    }
  } finally {
    if (search === lastSearch) {
      searching.value = false
    }
  }
}

// Called when the user changes the search, so a picked address can't go stale
function clearSelectedAddress() {
  addressForm.value.selected = undefined
  customerForm.value.address1 = ''
  customerForm.value.zipCode = ''
  customerForm.value.city = ''
}

// The "Ryd" button: empties the postnummer, the address and the picked address.
// Set to undefined rather than '' so the fields don't show "Påkrævet" right away
function clearAll() {
  clearSelectedAddress()
  addressForm.value = {}
}

// What is left of the titel after the adresselinje is ", postnummer city", e.g. ", 4000 Roskilde".
// vejnavn and vejnavnhusnummer have no postnummer
function splitTitel(option: AddressSearchResult) {
  const [, zipCode, city] = option.titel.slice(option.adresselinje.length).match(/^,?\s*(\d{4})\s*(.*)$/) ?? []

  return { zipCode, city }
}

function onSelectAddress(option: AddressSearchResult) {
  // Anything but an address narrows the search to it: a street name ("lundeh" -> Lundehusene, Lundemosen), a street in
  // one postnummer, or a house number with more than one address (floors/doors). The postnummer is added when searching,
  // so only the street and house number go in the text, e.g. "Nørrebrogade 12"
  if (option.type !== 'adresse') {
    const postnummer = option.postnr ?? splitTitel(option).zipCode

    clearSelectedAddress()
    addressForm.value.tekst = [option.vejnavn, option.husnummer].filter(Boolean).join(' ')

    if (postnummer) {
      addressForm.value.postnummer = postnummer
    }

    return
  }

  const { zipCode, city } = splitTitel(option)

  // Split like the fields: the postnummer in its own, the rest of the address (without the city) in the text
  addressForm.value.selected = option
  addressForm.value.postnummer = zipCode
  addressForm.value.tekst = option.adresselinje
  customerForm.value.address1 = option.adresselinje
  customerForm.value.zipCode = zipCode
  customerForm.value.city = city
}

// Called by CustomerView when the email belongs to a customer. Their saved address replaces whatever was typed or picked,
// and the fields are locked so the lookup isn't used
function useCustomerAddress(customer: CustomerResponse) {
  const address1 = customer.address1?.trim()
  const zipCode = customer.zipcode?.trim()

  // Without a full address there is nothing to fill, so it is picked as usual
  if (!address1 || !/^\d{4}$/.test(zipCode ?? '')) {
    clearCustomerAddress()
    return
  }

  addressForm.value.selected = undefined
  addressForm.value.postnummer = zipCode
  addressForm.value.tekst = address1
  addressForm.value.fromCustomer = true
  customerForm.value.address1 = address1
  customerForm.value.zipCode = zipCode
  customerForm.value.city = customer.city?.trim() ?? ''
}

// Called by CustomerView when the email no longer belongs to a customer: their address goes and the lookup is back
function clearCustomerAddress() {
  if (addressForm.value.fromCustomer) {
    clearAll()
  }
}

defineExpose({ useCustomerAddress, clearCustomerAddress })

watch(() => [
  addressForm.value.postnummer,
  addressForm.value.tekst
], () => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(searchAddresses, 300)
})
</script>

<template>
  <InputCardComponent label="Adresse">
    <template #actions>
      <v-btn
        prepend-icon="mdi-filter-remove-outline"
        text="Ryd"
        variant="text"
        size="small"
        :disabled="addressForm.fromCustomer"
        @click="clearAll"
      />
    </template>

    <!-- Disabled while the address comes from the customer found by email -->
    <v-text-field
      v-model="addressForm.postnummer"
      label="Postnummer"
      name="zipCode"
      type="text"
      inputmode="numeric"
      autocomplete="postal-code"
      maxlength="4"
      :rules="[rules.required, rules.postnummer]"
      :disabled="addressForm.fromCustomer"
      :clearable="!addressForm.fromCustomer"
      :append-inner-icon="addressForm.fromCustomer ? 'mdi-lock-outline' : undefined"
      @beforeinput="onlyDigits"
      @update:model-value="clearSelectedAddress"
    >
      <template #append>
        <HelpTooltipComponent :text="helpTexts.postnummer" />
      </template>
    </v-text-field>

    <!-- No counter once an address is picked or found, its adresselinje can be longer than what can be typed -->
    <v-text-field
      v-model="addressForm.tekst"
      label="Adresse"
      name="addressText"
      type="text"
      :maxlength="tekstMaxLength"
      :counter="hasAddress ? undefined : tekstMaxLength"
      placeholder="F.eks. Adresse vej 42B, 9, th"
      :disabled="!postnummerValid || addressForm.fromCustomer"
      :rules="[rules.required, rules.tekst]"
      :clearable="!addressForm.fromCustomer"
      :append-inner-icon="addressForm.fromCustomer ? 'mdi-lock-outline' : undefined"
      @update:model-value="clearSelectedAddress"
    >
      <template #append>
        <HelpTooltipComponent :text="helpTexts.tekst" />
      </template>
    </v-text-field>

    <v-progress-linear
      v-if="searching"
      color="primary"
      indeterminate
    />

    <v-alert
      v-if="addressForm.fromCustomer"
      type="info"
      variant="tonal"
      density="compact"
      text="Adressen er hentet ud fra din email."
    />

    <!-- Hidden once an address is picked or found, it comes back when the postnummer or address is changed -->
    <template v-if="!hasAddress && canSearch && searched && !searching">
      <v-list
        v-if="addressOptions.length"
        class="border rounded"
        density="compact"
        max-height="320"
      >
        <v-list-subheader>
          Vælg din adresse
          <HelpTooltipComponent :text="helpTexts.adresse" />
        </v-list-subheader>
        <!-- vejnavn and vejnavnhusnummer have no id, their titel is unique in a result -->
        <v-list-item
          v-for="option in addressOptions"
          :key="option.id || option.titel"
          :title="option.titel"
          @click="onSelectAddress(option)"
        >
          <template #append>
            <!-- A street or a house number with several addresses: picking it narrows the search -->
            <v-icon v-if="option.type !== 'adresse'">mdi-chevron-right</v-icon>
          </template>
        </v-list-item>
      </v-list>
      <!-- The API sends at most 100 -->
      <div
        v-if="addressOptions.length >= 100"
        class="text-caption mt-2"
      >
        Viser de første 100 adresser. Skriv mere af adressen for at finde din.
      </div>
      <v-alert
        v-if="!addressOptions.length"
        type="info"
        variant="tonal"
        density="compact"
        text="Vi kunne ikke finde adressen. Tjek postnummer og adresse."
      />
    </template>
  </InputCardComponent>
</template>
